// :cow: Cosmose CONFIDENTIAL :iso:
import { Injectable } from '@angular/core';
import { HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Observable, of, Subject, throwError } from 'rxjs';
import { Store } from '@ngrx/store';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { loggedOut, tokenRefreshed } from '@coa/app/auth/store/auth.actions';
import { AppConfig } from '../config/appConfig';
import { ConfigService } from '@coa/app/core/services/config.service';
import { AuthService } from '@coa/app/libs/auth/auth-service/services/auth.service';
import { LoginResponse } from '@coa/app/libs/auth/auth-service/models/login-response';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  refreshTokenInProgress = false;
  tokenRefreshedSource = new Subject<string>();
  tokenRefreshed$ = this.tokenRefreshedSource.asObservable();

  constructor(
    private authService: AuthService,
    private store$: Store,
    private configService: ConfigService,
  ) {
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<any> {
    const accessToken = localStorage.getItem(AppConfig.accessToken);
    const refreshToken = localStorage.getItem(AppConfig.refreshToken);

    return next.handle(this.addAuthHeaders(request, accessToken)).pipe(
      catchError((error) => {
        if (error.status === 401) {
          if (error.error.error_description.includes('Invalid refresh token') || error.error.error_description.includes('Cannot convert')) {
            this.store$.dispatch(loggedOut());
          } else {
            return this.refreshToken(refreshToken).pipe(
              switchMap((newToken: string) => {
                return next.handle(this.addAuthHeaders(request, newToken));
              }),
              catchError(() => {
                this.store$.dispatch(loggedOut());
                return of();
              }));
          }
        }
        return throwError(error);
      }));
  }

  addAuthHeaders(req: HttpRequest<any>, token: string) {
    if (req.url.includes(this.configService.getConfig().analyticsApi) || req.url.includes(this.configService.getConfig().clientServiceApi)) {
      return req.clone({
        setHeaders: {
          Authorization: `${ this.configService.getConfig().prefix } ${ token }`,
        },
      });
    }
    return req;
  }

  private refreshToken(refreshToken: string): Observable<string> {
    if (this.refreshTokenInProgress) {
      return new Observable(observer => {
        this.tokenRefreshed$
          .subscribe((token) => {
            observer.next(token);
            observer.complete();
          });
      });
    } else {
      this.refreshTokenInProgress = true;
      return this.authService.refreshToken(refreshToken).pipe(
        tap((response: LoginResponse) => {
          this.store$.dispatch(tokenRefreshed({
            accessToken: response.access_token,
            refreshToken: response.refresh_token,
          }));
          this.refreshTokenInProgress = false;
          this.tokenRefreshedSource.next(response.access_token);
        }),
        map((response: LoginResponse) => response.access_token),
      );
    }
  }
}
